import moment from "moment";
import React, { JSX } from "react";
import Barcode from "react-barcode";
import { QRCodeSVG } from "qrcode.react";
import { capitalize, startCase } from "lodash";
import { Box, Typography } from "@mui/material";
import hasOwnProperty from "../../../../helpers/object";
import { LabRecord, LabTest } from "../../../../interfaces/Lab";
import { getTestReportBaseUrl } from "../../../../helpers/urls";
import { InfoField, scaleFont } from "../../../../components/Print/Print";
import { Vaccination } from "../../../../interfaces/AssessmentInterfaces";
import { SubRow } from "./DefaultPrintComponents";

export const SectionTitle = ({
  title,
  isSmall,
  hasTopMargin,
  style = {}
}: {
  title: string;
  isSmall?: boolean;
  hasTopMargin?: boolean;
  style?: React.CSSProperties;
}): JSX.Element => (
  <Box sx={{ position: "relative", mt: hasTopMargin ? 2 : 0, ...style }}>
    <Box sx={{ position: "absolute", top: "50%", width: "100%", borderTop: "1px solid #ddd" }} />
    <Typography
      component="span"
      variant="h6"
      sx={{ position: "relative", backgroundColor: "#fff", fontSize: isSmall ? 14 : 15, pr: 1 }}
    >
      {title}
    </Typography>
  </Box>
);

// customized info field
const CustomizedInfoField = ({
  label,
  data,
  smallWidth = false,
  valueStyle = {},
  labelStyle = {},
  containerStyle = {}
}: {
  label: string;
  data: string | number | null;
  smallWidth?: boolean;
  valueStyle?: React.CSSProperties;
  labelStyle?: React.CSSProperties;
  containerStyle?: React.CSSProperties;
}): JSX.Element => (
  <Box>
    {data && (
      <InfoField
        label={label}
        data={data}
        labelStyle={{
          ...labelStyle,
          width: smallWidth ? "88px" : "130px",
          fontSize: scaleFont("0.28cm", 1)
        }}
        valueStyle={{ fontSize: scaleFont("0.28cm", 1), ...valueStyle }}
        containerStyle={containerStyle}
      />
    )}
  </Box>
);

export const VitalsInfo = ({
  labRecord,
  recentAssessment
}: {
  labRecord: LabRecord;
  recentAssessment: any;
}): JSX.Element | null => {
  const vitalRows =
    (labRecord?.results?.vitals || recentAssessment?.vitals)
      ?.slice()
      ?.sort((a, b) => (a.reading || "").localeCompare(b.reading || ""))
      ?.slice(0, 12) || [];
  if (!vitalRows.length) {
    return null;
  }
  return (
    <Box
      sx={{
        mt: 0.5,
        display: "grid",
        gridTemplateColumns: "repeat(6, 1fr)",
        rowGap: 1.5,
        border: "0.5px solid",
        borderColor: "divider",
        borderRadius: 1,
        px: 1.5,
        py: 1
      }}
    >
      {vitalRows.map((vital, index) => {
        const isLastInRow = (index + 1) % 6 === 0;
        const isLastItem = index === vitalRows.length - 1;
        return (
          <Box
            key={vital.name}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              borderRight: isLastInRow || isLastItem ? "none" : "0.5px solid",
              borderColor: "divider",
              px: 1
            }}
          >
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {startCase(vital.name)}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {vital.reading || ""}
              {vital.unit && (
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ color: "text.secondary", ml: 0.5 }}
                >
                  {vital.unit}
                </Typography>
              )}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export const ClientInfo = ({
  labRecord,
  showReferrer,
  showBarCode
}: {
  labRecord: LabRecord;
  showReferrer: boolean;
  showBarCode?: boolean;
}): JSX.Element => {
  const { client } = labRecord;
  const clientName = `${capitalize(client?.firstName)} ${capitalize(client?.lastName)}`;
  const getFormattedDateInAD = (date: string): string => `${moment(date).format("YYYY-MM-DD")}`;

  const gender = (() => {
    if (client?.gender === "1") {
      return "Male";
    }
    if (client?.gender === "2") {
      return "Female";
    }
    return "-";
  })();

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        p: "0.2cm",
        minHeight: "2.3cm",
        width: "100%"
      }}
    >
      <Box sx={{ maxWidth: "6cm" }}>
        <CustomizedInfoField label="S.N." data={labRecord.labIdentifier || ""} smallWidth />
        <InfoField
          label="Name:"
          data={clientName}
          labelStyle={{ width: "2.3cm", fontSize: scaleFont("0.28cm", 1), paddingTop: "0.1cm" }}
          valueStyle={{
            fontSize: scaleFont("0.34cm", 1),
            fontWeight: "700",
            lineHeight: "1"
          }}
        />
        <CustomizedInfoField label="Marital status:" data={client.maritalStatus} smallWidth />
        <CustomizedInfoField label="Address:" data={client.address} smallWidth />
        {showBarCode && (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <CustomizedInfoField label="Test Id:" data={labRecord.id} smallWidth />
            <Barcode
              value={String(labRecord.id)}
              width={2}
              displayValue={false}
              fontSize={5}
              height={20}
              margin={2}
              font="arial"
            />
          </Box>
        )}
      </Box>
      <Box>
        <CustomizedInfoField
          label="Age:"
          data={client.dob ? moment().diff(client.dob, "years") : ""}
          smallWidth
        />
        <CustomizedInfoField
          label="Passport no:"
          data={client?.passportInfo && client?.passportInfo?.passportNumber}
          smallWidth
        />
        <CustomizedInfoField label="Nationality:" data={client.nationality} smallWidth />
        <CustomizedInfoField
          label="Applied to:"
          data={client.appliedCountry}
          smallWidth
          valueStyle={{ fontSize: scaleFont("0.34cm", 1), fontWeight: "700" }}
        />
        <CustomizedInfoField
          label="Exam date:"
          data={getFormattedDateInAD(labRecord?.results?.examDate || new Date().toISOString())}
          smallWidth
        />
      </Box>
      <Box>
        <CustomizedInfoField label="Gender:" data={gender} />
        <CustomizedInfoField label="Occupation:" data={client.occupation} />
        <CustomizedInfoField
          label="Passport issue place:"
          data={client.passportInfo?.issuingAuthority || ""}
        />
        <CustomizedInfoField
          label="Passport issue date:"
          data={
            client.passportInfo?.issueDate
              ? moment(client.passportInfo?.issueDate).format("YYYY-MM-DD")
              : ""
          }
        />
        <CustomizedInfoField
          label="Passport expiration date:"
          data={
            client.passportInfo?.expiryDate
              ? moment(client.passportInfo?.expiryDate).format("YYYY-MM-DD")
              : ""
          }
        />
        {showReferrer && (
          <CustomizedInfoField label="Recruiting agency:" data={labRecord.referrers || ""} />
        )}
      </Box>
    </Box>
  );
};

// sub row
export const SubRowNonBordered = ({
  lt,
  children = <></>,
  isChild = false
}: {
  lt: LabTest;
  children?: React.ReactNode;
  isChild?: boolean;
}): JSX.Element => {
  const isMainTest = !lt?.formData?.reading && lt.subTests?.length;
  return (
    <>
      <Box
        sx={{
          flexBasis: "50%",
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          gap: 0.5
        }}
      >
        <Box
          sx={{
            padding: "0cm 0.1cm",
            width: isMainTest ? "100%" : "75%"
          }}
        >
          {isMainTest ? (
            <SectionTitle
              isSmall
              title={lt.name}
              style={{
                paddingLeft: isChild ? "0.2cm" : "1px"
              }}
            />
          ) : (
            <Typography
              sx={{
                fontSize: scaleFont("0.33cm", 1),
                paddingLeft: isChild ? "0.2cm" : "1px"
              }}
            >
              {lt.name}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            padding: "0cm 0.1cm",
            width: !isMainTest ? "25%" : "0"
          }}
        >
          <Typography
            sx={{
              fontSize: scaleFont("0.33cm", 1),
              textAlign: "right"
            }}
          >
            {hasOwnProperty(lt, "formData") && lt.formData.reading}
          </Typography>
        </Box>
      </Box>
      {children}
    </>
  );
};

// sub row
export const SubRowNonBorderedInnerMostChild = ({ lt }: { lt: LabTest }): JSX.Element => (
  <Box
    sx={{
      flexBasis: "50%",
      width: "100%",
      display: "flex",
      justifyContent: "space-between",
      gap: 0.5
    }}
  >
    <Box
      sx={{
        padding: "0cm 0.1cm",
        width: "75%"
      }}
    >
      <Typography
        sx={{
          fontSize: scaleFont("0.33cm", 1),
          paddingLeft: "0.3cm"
        }}
      >
        {lt.name}
      </Typography>
    </Box>
    <Box
      sx={{
        padding: "0cm 0.1cm",
        width: "25%"
      }}
    >
      <Typography
        sx={{
          fontSize: scaleFont("0.33cm", 1),
          textAlign: "right"
        }}
      >
        {hasOwnProperty(lt, "formData") && lt.formData.reading}
      </Typography>
    </Box>
  </Box>
);

// client image
export const ClientProfileImage = ({
  profileImage
}: {
  profileImage: string | null;
}): JSX.Element => (
  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
    {profileImage ? (
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end"
        }}
      >
        <img
          src={profileImage}
          style={{
            height: "3cm",
            width: "2.5cm",
            objectFit: "cover"
          }}
          alt="profileImage"
        />
      </Box>
    ) : (
      <Box
        sx={{
          height: "3cm",
          width: "2.5cm",
          border: "1px solid lightgrey",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Typography sx={{ fontSize: "0.2cm", textAlign: "center" }}>
          Photo
          <br />
          2.5*3cm
        </Typography>
      </Box>
    )}
  </Box>
);

// present health status
export const PresentHealthStatus = ({
  recentAssessment
}: {
  recentAssessment: any;
}): JSX.Element => (
  <Typography
    sx={{
      textAlign: "center",
      fontWeight: 900,
      fontSize: "0.40cm"
    }}
  >
    {recentAssessment?.presentHealthStatus && (
      <Box component="span" sx={{ p: "0 0.1cm", border: "0.5px solid black" }}>
        {recentAssessment?.presentHealthStatus.toUpperCase()}
      </Box>
    )}
  </Typography>
);

// client illness div
export const ClientIllnessesDiv = ({
  recentAssessment,
  hidePresentHealthStatus = false
}: {
  recentAssessment: any;
  hidePresentHealthStatus?: boolean;
}): JSX.Element => (
  <>
    {recentAssessment && (
      <Box sx={{ width: "100%", mt: "0.44cm" }}>
        {!hidePresentHealthStatus && (
          <Box sx={{ marginLeft: "2.5cm", marginTop: "-0.2cm" }}>
            <PresentHealthStatus recentAssessment={recentAssessment} />
          </Box>
        )}

        <Typography
          sx={{
            fontSize: scaleFont("0.28cm"),
            fontWeight: 600,
            marginTop: `${recentAssessment?.presentHealthStatus ? "-0.4cm" : "10px"}`
          }}
        >
          General Examination
        </Typography>

        <Box>
          <Typography sx={{ fontSize: "0.28cm", fontWeight: 600, width: "115%" }}>
            Past history of serious illness, major surgery and significant psychological problem
            including (Epilepsy and Depression):{" "}
            <span style={{ fontWeight: 400 }}>
              {recentAssessment?.hopi && recentAssessment.hopi}
            </span>
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: "0.28cm", fontWeight: 600 }}>
            Past history of allergy :{" "}
            <span style={{ fontWeight: 400 }}>
              {recentAssessment?.pastHistoryOfAllergy && recentAssessment.pastHistoryOfAllergy}
            </span>
          </Typography>
        </Box>
      </Box>
    )}
  </>
);

// vaccination sub row
export const VaccinationSubRow = ({ vaccination }: { vaccination: Vaccination }): JSX.Element => (
  <Box sx={{ display: "flex", width: "100%", justifyContent: "space-between", gap: 0.5 }}>
    <Box
      sx={{
        padding: "0cm 0.1cm",
        width: "50%"
      }}
    >
      <Typography sx={{ fontSize: scaleFont("0.33cm", 1) }}>{vaccination.name}</Typography>
    </Box>
    <Box
      sx={{
        padding: "0cm 0.1cm",
        width: "25%"
      }}
    >
      <Typography sx={{ fontSize: scaleFont("0.33cm", 1) }}>
        {vaccination.yesno ? "Yes" : "No"}
      </Typography>
    </Box>
    <Box padding="0cm 0.1cm" width="25%">
      <Typography sx={{ fontSize: scaleFont("0.33cm", 1) }}>
        {moment(vaccination.date).format("YYYY-MM-DD")}
      </Typography>
    </Box>
  </Box>
);

// Authorized sp info
export const AuthorizedSpInfo = ({ sp }: { sp: any }): JSX.Element => (
  <Box sx={{ marginTop: "0.2cm", flexBasis: "50%" }}>
    <Box sx={{ height: "2cm", marginBottom: "0.2cm" }}>
      {sp?.signature && (
        <img
          style={{ display: "block" }}
          src={sp.signature}
          height="40px"
          width="80px"
          alt="sp-signature"
        />
      )}
    </Box>
    <Box
      sx={{
        pt: 0.5,
        borderTop: "1px solid #dddddd"
      }}
    >
      <Typography
        sx={{
          fontSize: "0.28cm",
          textAlign: "left",
          fontWeight: 600
        }}
      >
        {startCase(sp.title)} {startCase(sp.name)}
      </Typography>
      {sp.designation && (
        <Typography sx={{ whiteSpace: "pre-wrap", fontSize: "0.25cm" }}>
          {sp.designation}
        </Typography>
      )}
    </Box>
  </Box>
);

export const RenderNamedBlockTestGroup = ({
  blockName,
  testGroupData,
  gender,
  namePlacement = "top"
}: {
  blockName: string;
  testGroupData: any;
  gender: "1" | "2" | null;
  namePlacement?: "top" | "left";
}): JSX.Element => (
  <Box
    sx={{
      width: "100%",
      marginTop: "0.10cm",
      display: "flex",
      flexDirection: namePlacement === "top" ? "column" : "row"
    }}
  >
    <Box
      sx={{
        ...(namePlacement === "left"
          ? {
              width: "30px",
              writingMode: "vertical-rl",
              transform: "rotate(180deg)",
              border: "1px solid black",
              borderLeft: 0,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center"
            }
          : {
              border: "none"
            }),
        textTransform: "uppercase",
        fontWeight: 600,
        fontSize: "0.28cm"
      }}
    >
      {blockName}
    </Box>
    <Box
      sx={{
        height: "100%",
        width: "100%",
        flex: 1
      }}
    >
      {testGroupData.map((lt, index, array) => {
        const isLastGroup = index === array.length - 1;
        const isLast = isLastGroup && lt.subTests?.length === 0;
        return (
          <SubRow key={lt.id} lt={lt} isLast={isLast} gender={gender}>
            {lt.subTests?.map((sT, i, arr) => (
              <SubRow
                key={sT.id}
                lt={sT}
                isChild
                isLast={isLastGroup && i === arr.length - 1}
                gender={gender}
              />
            ))}
          </SubRow>
        );
      })}
    </Box>
  </Box>
);

// main lab data display div
export const MainLabDataDisplayDiv = ({
  labRecord,
  recentAssessment,
  showQrCode,
  showMedicalDisclaimerOnFooter
}: {
  labRecord: LabRecord;
  recentAssessment: any;
  showQrCode: boolean;
  showMedicalDisclaimerOnFooter?: boolean;
}): JSX.Element => {
  const { client } = labRecord;

  const getClientGender = (clientGender: string): string => {
    if (clientGender === "1") {
      return "m";
    }
    if (clientGender === "2") {
      return "f";
    }
    return "-";
  };

  const gender = getClientGender(client.gender);

  // eslint-disable-next-line no-nested-ternary
  const greeting = gender === "m" ? "Sir" : gender === "f" ? "Madam" : "Sir/Madam";

  const clientName = `${capitalize(client.firstName)} ${capitalize(client.lastName)}`;
  const labTests = (labRecord.results?.data.length && labRecord.results.data[0]?.labTests) || [];
  const getRequiredCategoryTests = (cName) => labTests.filter((item) => item.category === cName);

  const [
    systemeticGroup,
    hematologyGroup,
    bioChemGroup,
    urineGroup,
    stoolGroup,
    serologyGroup,
    radiologyGroup,
    specialGroup
  ] = [
    "Systemic",
    "Haematology",
    "Biochemistry",
    "Urine Examination",
    "Stool Examination",
    "Serology",
    "Radiology",
    "Special"
  ].map((item) => getRequiredCategoryTests(item));
  const clinicalImpression = specialGroup.filter((item) => item.name === "Clinical impression");
  radiologyGroup.push(clinicalImpression[0]);
  const { authorizedSP1, authorizedSP2 } = labRecord?.results?.approvedBy || {};

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          gap: 2
        }}
      >
        <Box sx={{ width: "49%" }}>
          <Box
            sx={{
              marginTop: "0.04cm",
              marginRight: "4px"
            }}
          >
            {systemeticGroup.map((lt) => {
              const subTestHasSubTest = lt.subTests?.some((item) => item.subTests?.length);
              return (
                <Box key={lt.id}>
                  <SubRowNonBordered lt={lt}>
                    <Box sx={subTestHasSubTest ? {} : { display: "flex", gap: 2 }}>
                      {lt.subTests?.map((sT) => (
                        <SubRowNonBordered lt={sT} isChild key={sT.id}>
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "repeat(2, 1fr)"
                            }}
                          >
                            {sT?.subTests?.map((ssT) => (
                              <SubRowNonBorderedInnerMostChild lt={ssT} key={ssT.id} />
                            ))}
                          </Box>
                        </SubRowNonBordered>
                      ))}
                    </Box>
                  </SubRowNonBordered>
                </Box>
              );
            })}
          </Box>
          {recentAssessment.remarks && (
            <Typography sx={{ fontSize: "0.3cm" }}>Remarks: {recentAssessment.remarks}</Typography>
          )}
        </Box>
        <Box width="49%">
          <Box>
            {/* Radiology */}
            <SectionTitle title="RADIOLOGY" />

            <Box mt="0.06cm" mr="4px">
              {radiologyGroup.map((lt) => (
                <SubRowNonBordered key={lt.id} lt={lt}>
                  {lt.subTests?.map((sT) => (
                    <SubRowNonBordered key={sT.id} lt={sT} isChild />
                  ))}
                </SubRowNonBordered>
              ))}
            </Box>
            {/* Serology */}
            <SectionTitle hasTopMargin title="SEROLOGY" />

            <Box mt="0.06cm" mr="4px">
              {serologyGroup.map((lt) => (
                <SubRowNonBordered key={lt.id} lt={lt}>
                  {lt.subTests?.map((sT) => (
                    <SubRowNonBordered key={sT.id} lt={sT} isChild />
                  ))}
                </SubRowNonBordered>
              ))}
            </Box>

            {/* Biochem */}
            <SectionTitle hasTopMargin title="BIOCHEMISTRY" />

            <Box mt="0.06cm" mr="4px">
              {bioChemGroup.map((lt) => (
                <SubRowNonBordered key={lt.id} lt={lt}>
                  {lt.subTests?.map((sT) => (
                    <SubRowNonBordered key={sT.id} lt={sT} isChild />
                  ))}
                </SubRowNonBordered>
              ))}
            </Box>

            {labRecord?.results?.vaccinations?.length > 0 && (
              <>
                <SectionTitle hasTopMargin title="VACCINATION STATUS" />

                <Box
                  sx={{
                    mt: "0.06cm",
                    mr: "4px"
                  }}
                >
                  {labRecord?.results?.vaccinations?.map((vaccination, index, array) => (
                    <VaccinationSubRow
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                      vaccination={vaccination}
                      isLast={index === array.length - 1}
                    />
                  ))}
                </Box>
              </>
            )}
            {/* remarks recommend */}
            <Box sx={{ marginTop: "0.25cm" }}>
              <SectionTitle hasTopMargin title="Remarks" isSmall />
              <Typography sx={{ fontSize: "0.3cm" }}>Dear {greeting},</Typography>
              <Typography sx={{ fontSize: "0.3cm", width: "92%" }}>
                The above mentioned medical report of {clientName} is{" "}
                <b>{recentAssessment.presentHealthStatus}</b> for the mentioned job.
              </Typography>
            </Box>
            {/* sps */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-end",
                gap: 4
              }}
            >
              {authorizedSP1 && <AuthorizedSpInfo sp={authorizedSP1} />}
              {authorizedSP2 && <AuthorizedSpInfo sp={authorizedSP2} />}
            </Box>
            <Box sx={{ marginTop: "0.35cm", display: "flex", gap: "0.25cm" }}>
              <div>
                {showQrCode && (
                  <QRCodeSVG size={70} value={`${getTestReportBaseUrl()}/${labRecord.uuid}`} />
                )}
              </div>

              {!showMedicalDisclaimerOnFooter && (
                <Typography
                  sx={{
                    fontSize: "0.25cm",
                    border: "1px solid #f0f0f0",
                    padding: "0.15cm",
                    marginRight: "0.25cm"
                  }}
                >
                  Recommended by Ministry of Health and Population, Government of Nepal. This report
                  is valid for two months from the date of Medical Examination.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        {/* second page */}
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 2,
          "@media print": {
            breakInside: "avoid",
            pageBreakInside: "avoid",
            breakBefore: "page", // force new page before this block
            pageBreakBefore: "always" // legacy equivalent
          }
        }}
      >
        <Box flexBasis="49%">
          <Box width="100%" display="flex">
            <Box width="100%" display="flex">
              <Typography sx={{ width: "50%", fontWeight: 700, fontSize: "0.28cm" }}>
                Examination
              </Typography>
              <Typography sx={{ width: "25%", fontWeight: 700, fontSize: "0.28cm" }}>
                Reading
              </Typography>
              <Typography sx={{ width: "25%", fontWeight: 700, fontSize: "0.28cm" }}>
                Range
              </Typography>
            </Box>
          </Box>
          <RenderNamedBlockTestGroup
            blockName="Hematology"
            testGroupData={hematologyGroup}
            gender={client.gender}
          />
        </Box>
        <Box flexBasis="49%">
          <Box width="100%" display="flex">
            <Box width="100%" display="flex">
              <Typography sx={{ width: "50%", fontWeight: 700, fontSize: "0.28cm" }}>
                Examination
              </Typography>
              <Typography sx={{ width: "25%", fontWeight: 700, fontSize: "0.28cm" }}>
                Reading
              </Typography>
              <Typography sx={{ width: "25%", fontWeight: 700, fontSize: "0.28cm" }}>
                Range
              </Typography>
            </Box>
          </Box>
          <RenderNamedBlockTestGroup
            blockName="Urine Analysis"
            testGroupData={urineGroup}
            gender={client.gender}
          />
          <RenderNamedBlockTestGroup
            blockName="Stool Analysis"
            testGroupData={stoolGroup}
            gender={client.gender}
          />
        </Box>
      </Box>
    </Box>
  );
};
